
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useSpecialClassReports = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [search, setSearch] = useState("");
  const [showNotApproveOnly, setShowNotApproveOnly] = useState(true);
  const queryClient = useQueryClient();

  // Function to fetch jobs
  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("job_student_view")
        .select("*")
        .eq("is_special_job", true)
        .order("start_at", { ascending: false });

      if (showNotApproveOnly) {
        query = query.eq("approve_status", "pending");
      }

      if (search) {
        query = query.or(`student_names.ilike.%${search}%,teacher_name.ilike.%${search}%,teacher_code.ilike.%${search}%`);
      }

      const { data, error } = await query;

      if (error) {
        setError(error);
        toast({
          title: "Error",
          description: "Could not fetch special class jobs. Please try again.",
          variant: "destructive",
        });
      } else {
        setJobs(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("An unexpected error occurred"));
      toast({
        title: "Error",
        description: "Failed to connect to the database. Please check your internet connection.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [search, showNotApproveOnly]);

  // Fetch jobs on mount and when search or showNotApproveOnly changes
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Handle search submission
  const onSearch = () => {
    fetchJobs();
  };

  // Mutation for approving special classes
  const approveSpecialClassMutation = useMutation({
    mutationFn: async ({ jobId, status, note }: { jobId: number, status: "approved" | "rejected", note: string }) => {
      const { data, error } = await supabase.rpc('update_special_class_status', {
        p_job_id: jobId,
        p_status: status,
        p_note: note
      });
      
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["specialClasses"] });
      toast({
        title: "Success",
        description: "Special class status updated successfully",
      });
      fetchJobs();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update special class status: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    jobs,
    isLoading,
    error,
    search,
    setSearch,
    onSearch,
    showNotApproveOnly,
    setShowNotApproveOnly,
    approveSpecialClassMutation,
  };
};
